import { OrganizationSizes, OrganizationTypes } from '@platform/client';
import { OrganizationApplicationParameters } from '@platform/client/schemas';
import { FormsInputDropdown } from '@platform/components';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ApplicationPageProps } from '../../Application';
import { useApplicationLocalStorage } from '../../application-hook';
import { ApplicationBackground } from '../../ApplicationBackground';
import { ApplicationPageContent } from '../../components/ApplicationPageContent';
import BackgroundImage from '../../images/Heather-Watkins.svg';

type FormValues = {
  orgType: string;
  orgSize: string;
};

export const OrgTypeSelection = (props: ApplicationPageProps) => {
  const { onBack, onContinue, progress } = { ...props };

  const { updateApplicationData, getApplicationDataByKey } =
    useApplicationLocalStorage<OrganizationApplicationParameters>();

  const organizationSizes = OrganizationSizes.useGetOrganizationSizes().data || [];
  const organizationTypes = OrganizationTypes.useGetOrganizationTypes().data || [];

  const orgTypeItems = organizationTypes.map((type) => ({
    text: type.name || '',
    textValue: type.id,
  }));

  const orgSizeItems = organizationSizes
    .map((size) => ({
      text: size.name || '',
      textValue: size.id,
    }))
    .sort((a, b) => {
      const indexA = a.text.indexOf('-') > -1 ? a.text.indexOf('-') : a.text.indexOf('+');
      const indexB = b.text.indexOf('-') > -1 ? b.text.indexOf('-') : b.text.indexOf('+');
      const numA = Number(a.text.substring(0, indexA));
      const numB = Number(b.text.substring(0, indexB));
      return numA - numB;
    });

  const {
    handleSubmit,
    watch,
    formState: { errors },
    control,
  } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      orgType: getApplicationDataByKey('organizationType') || '',
      orgSize: getApplicationDataByKey('organizationSize') || '',
    },
  });

  const submit: SubmitHandler<FormValues> = (data) => {
    updateApplicationData({
      organizationType: data.orgType,
      organizationSize: data.orgSize,
    });
    onContinue();
  };

  const handleBack = () => {
    updateApplicationData({
      organizationType: watch('orgType'),
      organizationSize: watch('orgSize'),
    });
    onBack();
  };

  return (
    <ApplicationBackground
      progress={progress}
      isOrganization={true}
      onBack={handleBack}
      onSubmit={handleSubmit(submit)}
      backgroundImage={BackgroundImage}
      isEditProfile={props.isEditProfile}
    >
      <ApplicationPageContent
        title='Tell us a bit more about your organization'
        description={
          "These details help us understand your structure and scale. They'll also appear on your public profile."
        }
      >
        <FormsInputDropdown
          control={control}
          name='orgType'
          rules={{ required: 'Select your organization type to continue' }}
          label='Select your organization type'
          placeholder='Select one'
          items={orgTypeItems}
          type='default'
          error={errors.orgType}
          inputDisabled={true}
        />
        <FormsInputDropdown
          control={control}
          name='orgSize'
          rules={{
            required:
              'Select your organization size to continue. Only include direct employees, not contractors or volunteers',
          }}
          label='Select your organization size'
          placeholder='Select one'
          items={orgSizeItems}
          type='default'
          error={errors.orgSize}
          hintText='Only include direct employees, not contractors or volunteers'
          inputDisabled={true}
        />
      </ApplicationPageContent>
    </ApplicationBackground>
  );
};
